import { FC } from "react";

import { Box, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { CopyAddressButton } from "./CopyAddressButton/CopyAddressButton";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink, Location } from "@/icons/outlined";

const EXTERNAL_ICON_SELECTOR = "[data-external-icon]";
const COPY_BUTTON_SELECTOR = "[data-copy-address]";

const addressLinkSx = (theme: Theme) => ({
  alignSelf: "flex-start",
  color: "inherit",
  textDecoration: "none",
  [`& ${EXTERNAL_ICON_SELECTOR}`]: { visibility: "hidden" },
  "&:hover": {
    textDecoration: "underline",
    textDecorationColor: theme.palette.forms.text.intense,
    [`& ${EXTERNAL_ICON_SELECTOR}`]: { visibility: "visible" },
  },
});

interface AddressProps {
  label: string;
  mapsUrl: string;
  isPreview: boolean;
  textTypographySx: object;
  labelSx: object;
  onOpen: () => void;
  onCopy: () => void;
}

export const Address: FC<AddressProps> = ({ label, mapsUrl, isPreview, textTypographySx, labelSx, onOpen, onCopy }) => {
  const { t } = useTranslate();

  return (
    <Stack
      sx={{
        [`& ${COPY_BUTTON_SELECTOR}`]: { opacity: 0 },
        [`&:hover ${COPY_BUTTON_SELECTOR}, & ${COPY_BUTTON_SELECTOR}:focus-visible`]: { opacity: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <FormTypography sx={labelSx}>{t("organization", "address")}</FormTypography>
        {!isPreview && (
          <Box sx={{ ...labelSx, display: "flex", alignItems: "center" }}>
            <CopyAddressButton address={label} onCopy={onCopy} />
          </Box>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="flex-start"
        {...(isPreview
          ? {}
          : { component: "a" as const, href: mapsUrl, target: "_blank", rel: "noopener", onClick: onOpen })}
        sx={isPreview ? undefined : addressLinkSx}
        data-test="org-page-footer-address"
      >
        <Box
          sx={{
            ...textTypographySx,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: 24,
            height: "1lh",
            marginRight: { xs: 0.5, sm: 0 },
          }}
        >
          <FormIcon size="medium" sx={(theme: Theme) => ({ color: theme.palette.forms.text.moderate })}>
            <Location />
          </FormIcon>
        </Box>
        <FormTypography
          sx={(theme: Theme) => ({
            ...textTypographySx,
            color: theme.palette.forms.text.moderate,
            overflowWrap: "anywhere",
          })}
        >
          {label}
          {!isPreview && (
            <Box
              component="span"
              data-external-icon
              sx={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 0.5 }}
            >
              <FormIcon size="small" sx={(theme: Theme) => ({ color: theme.palette.forms.text.moderate })}>
                <ExternalLink />
              </FormIcon>
            </Box>
          )}
        </FormTypography>
      </Stack>
    </Stack>
  );
};
